// Block Contact Cards bio interactions (Figma 873:11464 modal, 617:6187 card hint). Two
// unrelated mechanisms sharing one file since both are "reveal a contact's bio":
//
// - List mode: [data-contact-toggle] (the single hint button - dots when closed, an X when
//   open, purely via CSS keyed off the aria-expanded this sets - see block-contact-cards.css)
//   opens/closes the card: [data-open] on its .contact-card-wrapper, which CSS animates (the
//   photo shrinks away, the sibling [data-contact-panel] unfolds), and `inert` on that panel while
//   it's closed. The panel is a normal-flow sibling after the card (no gap - reads as the card
//   growing in place, see block-contact-cards.css .contact-card-wrapper), so the toggle button
//   itself never moves. The toggle sits between the two in the DOM (pinned top-right by CSS), so
//   what it opens comes right after it in focus and reading order.
//   Accordion-style per the user - opening one panel closes whichever other panel was open,
//   found by data-* rather than tracked in a variable so this needs no per-instance state.
//   Clicking anywhere else on the card toggles it too, except on its email/phone links (a
//   card-wide target used to compete with them for taps on mobile) - see initListToggle.
// - Grid mode: [data-contact-dialog-open] opens the matching <dialog data-contact-dialog> via
//   showModal() - focus trap, Escape-to-close and the ::backdrop scrim are all native dialog
//   behavior, not hand-rolled here. [data-contact-dialog-close] and a backdrop click both close
//   it, and on mobile dragging the sheet's handle or header swipes it closed (initSheetDrag); the backdrop click is detected by comparing event.target against the dialog itself,
//   since a <dialog>'s own box IS the backdrop's hit area - a click landing inside
//   .contact-dialog__panel never reaches this listener as the dialog element itself.
//
// Engage "Contact/Open": the list toggle is tracked here, only when it opens (it also closes);
// the grid button only ever opens its dialog, so it's tracked via data-engage-* in the markup.

import { trackEngageEvent } from "../analytics/engage-events";

const WRAPPER_SELECTOR = ".contact-card-wrapper";

function closeAllPanels(root: ParentNode): void {
    root.querySelectorAll<HTMLElement>("[data-contact-panel]").forEach((panel) => {
        panel.inert = true;
        panel.closest(WRAPPER_SELECTOR)?.removeAttribute("data-open");
    });
    root.querySelectorAll<HTMLElement>("[data-contact-toggle]").forEach((toggle) => {
        toggle.setAttribute("aria-expanded", "false");
    });
}

// Returns whether the card opened (false when it closed or wasn't found).
function toggleCard(root: ParentNode, id: string): boolean {
    const panel = root.querySelector<HTMLElement>(`#contact-panel-${id}`);
    const wrapper = panel?.closest<HTMLElement>(WRAPPER_SELECTOR);
    if (!panel || !wrapper) {
        return false;
    }

    const willOpen = !wrapper.hasAttribute("data-open");
    closeAllPanels(root);

    if (willOpen) {
        panel.inert = false;
        wrapper.setAttribute("data-open", "");
        root.querySelectorAll<HTMLElement>(`[data-contact-toggle="${id}"]`).forEach((t) => {
            t.setAttribute("aria-expanded", "true");
        });
        afterUnfold(panel, () => revealPanel(panel));
    }

    return willOpen;
}

// Upper bound on waiting for the unfold to finish (CSS: 200ms) before scrolling anyway.
const UNFOLD_FALLBACK_MS = 250;

// revealPanel measures the panel's final height, which only exists once the unfold transition
// (block-contact-cards.css .contact-card__panel) has finished - so it runs on that transition's
// end, or right away when there's no transition (prefers-reduced-motion). The timeout covers an
// interrupted transition, e.g. the card being closed again mid-way.
function afterUnfold(panel: HTMLElement, callback: () => void): void {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        callback();
        return;
    }

    let done = false;
    const finish = (): void => {
        if (done) {
            return;
        }
        done = true;
        panel.removeEventListener("transitionend", onEnd);
        clearTimeout(timeout);
        callback();
    };
    const onEnd = (event: TransitionEvent): void => {
        if (event.target === panel && event.propertyName === "grid-template-rows") {
            finish();
        }
    };
    const timeout = setTimeout(finish, UNFOLD_FALLBACK_MS);
    panel.addEventListener("transitionend", onEnd);
}

// Scrolls just far enough to bring the opened bio's bottom into view - but never so far that
// the card's info section (name/title - .contact-card__info--list) leaves the top of the screen,
// so opening a card never makes the user scroll back up to see whose bio it is. The photo above
// it (mobile) may mostly scroll away - it's the name that matters - but a PHOTO_PEEK strip of it
// stays in view as a visual anchor (user's call).
// (Was scrollIntoView({ block: "nearest" }), which aligned the panel's bottom with the screen's
// bottom however tall the bio was.) Measured against the visual viewport (what's actually on
// screen), not innerHeight/0: on a page some other block makes wider than a phone screen, the
// layout viewport is stretched and the visible area starts partway down it (visualViewport
// .offsetTop) - measuring from 0 left the name ~120px above the screen in that case.
// UNVERIFIED - no Figma spec for this interaction.
const PHOTO_PEEK = 64;

function revealPanel(panel: HTMLElement): void {
    const card = panel.closest(WRAPPER_SELECTOR)?.querySelector(".contact-card--list");
    const info = card?.querySelector(".contact-card__info--list");
    if (!card || !info) {
        return;
    }

    const infoTop = info.getBoundingClientRect().top;
    // How much of the card sits above the info - the photo's height on mobile, 0 without a photo
    // or on desktop (photo beside the info) - so the peek never exceeds what's actually there.
    const aboveInfo = infoTop - card.getBoundingClientRect().top;
    const visibleTop = window.visualViewport?.offsetTop ?? 0;
    const visibleBottom = visibleTop + (window.visualViewport?.height ?? window.innerHeight);
    const hiddenBelow = panel.getBoundingClientRect().bottom - visibleBottom;
    const roomAbove = infoTop - visibleTop - Math.min(PHOTO_PEEK, aboveInfo);
    const distance = Math.min(hiddenBelow, roomAbove);

    if (distance > 0) {
        window.scrollBy({ top: distance, behavior: "smooth" });
    }
}

function initListToggle(root: ParentNode): void {
    root.querySelectorAll<HTMLButtonElement>("[data-contact-toggle]").forEach((toggle) => {
        toggle.addEventListener("click", (event) => {
            const id = toggle.dataset.contactToggle;
            const name = toggle.dataset.contactName;
            if (id && toggleCard(root, id) && name) {
                trackEngageEvent({ category: "Contact", action: "Open", label: name });
            }
            // Mouse/touch click (detail > 0; Enter/Space give 0): drop the focus the click left on
            // the button, or its focus styling keeps the hint dots showing after the pointer leaves.
            if (event.detail > 0) {
                toggle.blur();
            }
        });

        // The rest of the card toggles too, as a mouse/touch shortcut (2026-10-08) - except clicks
        // on its email/phone links (the reason the card-wide target was dropped before: it competed
        // with them for taps), and the click that ends a text selection, so names and numbers can
        // still be selected and copied. Deliberately no role/tabindex on the card: the hint button
        // stays the one real control for keyboard and screen readers, and a card-sized button
        // wrapping links would be nested interactive content (axe: nested-interactive).
        const card = toggle.closest(".contact-card-wrapper")?.querySelector<HTMLElement>(".contact-card--list");
        card?.addEventListener("click", (event) => {
            const target = event.target as Element | null;
            if (target?.closest("a") || window.getSelection()?.toString()) {
                return;
            }
            toggle.click();
        });
    });
}

// Mobile bottom sheet (Figma 873:11465) has no close button, only a drag handle - so dragging
// has to actually work: it pulls the sheet down with the pointer (never up past its resting
// position), and on release it either swipes off and closes, or springs back.
// The drag area is everything above the bio - the handle plus the header (name, title, email,
// phone) - not just the thin handle, which was too fiddly to hit (QA 2026-10-08). The bio below is
// the part that scrolls, so the two gestures never compete: the header has touch-action: none
// (block-contact-cards.css), the bio keeps native scrolling. A drag only starts after
// DRAG_START_DISTANCE of movement, so a plain tap on the header's email/phone links still clicks.
// UNVERIFIED thresholds (no Figma spec): dismiss past 25% of the sheet's height, or on a quick
// downward flick regardless of distance.
const DISMISS_DISTANCE_RATIO = 0.25;
const DISMISS_VELOCITY = 0.5; // px per ms
// A pointer that hasn't moved for this long before release isn't flicking any more: its last
// speed no longer counts (a quick 50px drag held still, then released, closed the sheet).
const VELOCITY_MAX_AGE_MS = 100;
const DRAG_START_DISTANCE = 6; // px
// Upper bound on waiting for the swipe-off (CSS: 200ms) before closing anyway.
const SLIDE_OUT_FALLBACK_MS = 300;
const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
const DRAG_ZONE_SELECTOR = ".contact-dialog__drag-handle, .contact-dialog__header";
const mobileSheet = window.matchMedia("(max-width: 767px)");

function initSheetDrag(dialog: HTMLDialogElement): void {
    let startY = 0;
    let lastY = 0;
    let lastTime = 0;
    let velocity = 0;
    let pointerId: number | null = null; // set while a press in the drag zone is in progress
    let dragging = false;
    // A swipe-off in progress (cancelled by every close, see below).
    let cancelSlideOut: (() => void) | null = null;

    const offset = (clientY: number): number => Math.max(0, clientY - startY);

    dialog.addEventListener("pointerdown", (event) => {
        const target = event.target as Element | null;
        if (!mobileSheet.matches || !event.isPrimary || !target?.closest(DRAG_ZONE_SELECTOR)) {
            return;
        }
        pointerId = event.pointerId;
        dragging = false;
        startY = lastY = event.clientY;
        lastTime = event.timeStamp;
        velocity = 0;
    });

    dialog.addEventListener("pointermove", (event) => {
        if (event.pointerId !== pointerId) {
            return;
        }
        const elapsed = event.timeStamp - lastTime;
        if (elapsed > 0) {
            velocity = (event.clientY - lastY) / elapsed;
        }
        lastY = event.clientY;
        lastTime = event.timeStamp;

        if (!dragging) {
            if (Math.abs(event.clientY - startY) < DRAG_START_DISTANCE) {
                return;
            }
            // Only now capture - capturing on pointerdown would retarget the release to the
            // dialog and swallow a tap on the header's links.
            dragging = true;
            dialog.style.transition = "none";
            dialog.setPointerCapture(event.pointerId);
        }
        dialog.style.translate = `0 ${offset(event.clientY)}px`;
    });

    const release = (event: PointerEvent): void => {
        if (event.pointerId !== pointerId) {
            return;
        }
        pointerId = null;
        if (!dragging) {
            return;
        }
        dragging = false;

        // The pointer was captured by the dialog, so the click that follows this release targets
        // the dialog itself - which the backdrop-click handler (initGridDialogs) reads as a click
        // outside and closes on. Swallow just that one click - it's dispatched in the same task as
        // the release, so the guard is dropped right after in case no click comes at all. Also
        // cancelled, not just stopped: a browser that sends it to the email/phone link the drag
        // started on would otherwise open the link (and Engage, listening on document in the
        // capture phase before this, would still track it - see engage-events.ts).
        const swallowClick = (clickEvent: MouseEvent): void => {
            clickEvent.preventDefault();
            clickEvent.stopImmediatePropagation();
        };
        dialog.addEventListener("click", swallowClick, { capture: true, once: true });
        window.setTimeout(() => dialog.removeEventListener("click", swallowClick, { capture: true }), 0);
        const distance = offset(event.clientY);
        const flickVelocity = event.timeStamp - lastTime > VELOCITY_MAX_AGE_MS ? 0 : velocity;
        const dismiss = distance > dialog.offsetHeight * DISMISS_DISTANCE_RATIO || flickVelocity > DISMISS_VELOCITY;

        // Back to the stylesheet's own translate transition (block-contact-cards.css) for the
        // swipe-off/spring-back - the drag itself ran with it disabled so the sheet tracks the
        // pointer 1:1.
        dialog.style.transition = "";
        if (!dismiss) {
            dialog.style.translate = "";
            return;
        }

        // Reduced motion: no slide (block-contact-cards.css has no transition then), just close.
        if (reducedMotion.matches) {
            dialog.close();
            return;
        }

        dialog.style.translate = "0 100%";
        // transitionend bubbles - only the sheet's own slide-out counts, not some child element's
        // own transition finishing mid-swipe. The timeout covers a transition that never runs
        // (nothing left to move) or never ends, which would leave the sheet open, off-screen, with
        // the page locked behind it.
        const onSlideOut = (transitionEvent: TransitionEvent): void => {
            if (transitionEvent.target === dialog && transitionEvent.propertyName === "translate") {
                dialog.close();
            }
        };
        const fallback = window.setTimeout(() => dialog.close(), SLIDE_OUT_FALLBACK_MS);
        cancelSlideOut = () => {
            dialog.removeEventListener("transitionend", onSlideOut);
            window.clearTimeout(fallback);
            cancelSlideOut = null;
        };
        dialog.addEventListener("transitionend", onSlideOut);
    };

    dialog.addEventListener("pointerup", release);
    dialog.addEventListener("pointercancel", release);

    // Every close path (swipe, Escape, backdrop) leaves the sheet reset for the next open - and
    // drops a swipe-off still waiting: closed some other way first (Escape mid-slide), its
    // transitionend listener would otherwise fire on the next open's slide-up and close it again.
    dialog.addEventListener("close", () => {
        cancelSlideOut?.();
        dialog.style.transition = "";
        dialog.style.translate = "";
    });
}

// Shows the panel's bottom fade (block-contact-cards.css .contact-dialog__panel--more) only while
// there's still content below the visible area - not on a bio short enough to fit, and not once
// scrolled to the end.
function initScrollFade(dialog: HTMLDialogElement): void {
    const panel = dialog.querySelector<HTMLElement>(".contact-dialog__panel");
    if (!panel) {
        return;
    }

    const update = (): void => {
        const remaining = panel.scrollHeight - panel.scrollTop - panel.clientHeight;
        panel.classList.toggle("contact-dialog__panel--more", remaining > 1);
    };

    panel.addEventListener("scroll", update, { passive: true });
    // Its size changes on open (display: none -> flex), window resize and the mobile/desktop
    // layout switch - a ResizeObserver covers all of them.
    new ResizeObserver(update).observe(panel);
}

function initGridDialogs(root: ParentNode): void {
    root.querySelectorAll<HTMLButtonElement>("[data-contact-dialog-open]").forEach((openButton) => {
        const id = openButton.dataset.contactDialogOpen;
        const dialog = id ? root.querySelector<HTMLDialogElement>(`#contact-dialog-${id}`) : null;
        if (!dialog) {
            return;
        }

        // Opened with a mouse/touch click (detail > 0; Enter/Space give 0)? Then, on close, the
        // focus the dialog hands back to the card is dropped again - left in place, its focus
        // styling kept the hint dots showing until the visitor clicked elsewhere. Keyboard users
        // keep it, so they land back on the card they came from.
        let openedByPointer = false;

        openButton.addEventListener("click", (event) => {
            openedByPointer = event.detail > 0;
            dialog.showModal();
        });

        dialog.addEventListener("close", () => {
            if (openedByPointer) {
                openButton.blur();
            }
        });

        dialog.querySelectorAll<HTMLButtonElement>("[data-contact-dialog-close]").forEach((closeButton) => {
            closeButton.addEventListener("click", () => {
                dialog.close();
            });
        });

        // A click on the <dialog> element itself only happens via the ::backdrop area - any
        // click inside .contact-dialog__panel/.contact-dialog__photo is caught by those
        // elements first and never bubbles to the dialog as target === dialog.
        dialog.addEventListener("click", (event) => {
            if (event.target === dialog) {
                dialog.close();
            }
        });

        initSheetDrag(dialog);
        initScrollFade(dialog);
    });
}

export function initContactCards(): void {
    document.querySelectorAll<HTMLElement>(".block-contact-cards--list").forEach((root) => {
        initListToggle(root);
    });
    document.querySelectorAll<HTMLElement>(".block-contact-cards--grid").forEach((root) => {
        initGridDialogs(root);
    });
}
